/** The public prospect upload portal (port of lead_upload/page.html,
 *  same address /upload/<slug> — Phase 5 of the port).
 *
 * No auth: the slug is the capability, and an unusable link reads as
 * absent — but ONLY a 404 from the config fetch means that; any other
 * failure is retried and then reads as a load error. Slot identity,
 * caps and labels come from /api/v1/upload/<slug>/config — the same
 * dict the Jinja page received as context. The upload machinery is the
 * Jinja page's verbatim: init → per-part presign+PUT with retry (4
 * attempts, exponential from 800ms) → complete, ETags collected when
 * CORS exposes them (the server lists parts from R2 when the client
 * set is incomplete), fail POSTed on abort so the sweep sees intent.
 * Slots run independently — one failing never blocks another.
 *
 * Built for slow, flaky links (Chattanooga FC, prod 2026-10-13: a 5 GB
 * video at 2-4 MB/s dropped at part 19, then at part 7, and each failure
 * threw away everything uploaded). So a part's PUT is patient — a fresh
 * address each attempt, a timeout, waiting out an offline browser, for up
 * to PART_RETRY_WINDOW_MS after its first failure — and a failure keeps
 * the parts R2 holds for a week: the upload is remembered (localStorage,
 * else this page's memory), and picking the same file again (or Resume)
 * sends only what is missing (/resume).
 */
import { useEffect, useRef, useState } from 'react'

import { API_BASE } from '../../lib/api'
import './lead-portal.css'

type SlotCfg = { allowed_extensions: string[]; max_bytes: number;
                 max_human: string; human_label: string }
type Config = { lead_name: string; slots: Record<string, SlotCfg> }

const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms))

async function withRetry<T extends { ok: boolean; status?: number }>(
  fn: () => Promise<T>, attempts = 4, baseDelay = 800): Promise<T> {
  let last: T | null = null
  for (let i = 0; i < attempts; i++) {
    try {
      last = await fn()
      if (last.ok) return last
      // A 4xx is a decision, not weather: a revoked link, a bad request,
      // an expired presign — repeating it buys ~5.6s of backoff for the
      // same answer. Only 5xx and network failures (status 0/undefined)
      // are worth retrying. (SDET review, PR #395.)
      if (last.status && last.status >= 400 && last.status < 500) return last
    } catch { /* a thrown error is a failed attempt */ }
    if (i < attempts - 1) await sleep(baseDelay * 2 ** i)
  }
  return last ?? ({ ok: false } as T)
}

async function postJSON(url: string, body: unknown):
    Promise<{ ok: boolean; status: number; data: Record<string, unknown> }> {
  const r = await fetch(url, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const data = await r.json().catch(() => ({}))
  return { ok: r.ok, status: r.status, data }
}

function putPart(url: string, blob: Blob, onProgress: (loaded: number) => void,
                 timeoutMs = PART_TIMEOUT_MS):
    Promise<{ ok: boolean; status: number; etag: string | null }> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url, true)
    // A stalled connection fails and is retried instead of hanging forever.
    // 32 MB at 100 kB/s is ~5.5 min; anything slower is not uploading.
    xhr.timeout = timeoutMs
    xhr.ontimeout = () => resolve({ ok: false, status: 0, etag: null })
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded) }
    xhr.onload = () => resolve({
      ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status,
      etag: xhr.getResponseHeader('ETag') || xhr.getResponseHeader('etag'),
    })
    xhr.onerror = () => resolve({ ok: false, status: 0, etag: null })
    xhr.send(blob)
  })
}

const PART_TIMEOUT_MS = 6 * 60 * 1000
/** Tries per part, and how long after a part's first failure it keeps
 *  trying: the backoff below waits 1+2+4+8+16+30… s, so ten tries fill
 *  about 2.5 min of waiting. Once a part has failed, everything after —
 *  backoff, waiting for the browser to come back online, and each retry's
 *  transfer (its timeout is what is left of the window, at least
 *  MIN_RETRY_SEND_MS) — fits in PART_RETRY_WINDOW_MS. So a part gives up
 *  at most PART_TIMEOUT_MS (its first try) + the window + MIN_RETRY_SEND_MS
 *  after it started: about 12.5 min on a dead line, not an hour. */
export const PART_ATTEMPTS = 10
export const PART_RETRY_WINDOW_MS = 5 * 60 * 1000
/** A retry's transfer always gets at least this long: 32 MB at ~350 kB/s. */
export const MIN_RETRY_SEND_MS = 90 * 1000
/** Wait before try `i` (0-based) after a failure: 1, 2, 4, 8, 16, 30, 30… s. */
export function retryDelay(i: number): number {
  return Math.min(30_000, 1000 * 2 ** i)
}
/** What a failed part means, in words for the alert and the page. */
export function describeFailure(status: number): string {
  if (status === 0) return 'network dropped (no response from storage)'
  if (status === 403) return 'storage refused the upload address (403)'
  return `storage answered ${status}`
}

/** Resolves once the browser says it is online again (or after `maxMs`,
 *  so a browser that never fires the event cannot stall the upload). */
function whenOnline(maxMs: number): Promise<void> {
  if (typeof navigator === 'undefined' || navigator.onLine !== false) return Promise.resolve()
  return new Promise((res) => {
    let timer = 0
    const done = () => {
      window.removeEventListener('online', done)
      window.clearTimeout(timer)
      res()
    }
    window.addEventListener('online', done)
    timer = window.setTimeout(done, maxMs)
  })
}

/** An interrupted upload, kept so the same file resumes rather than
 *  starting over. Keyed by link and slot; matched to the file by name,
 *  size and modification time. */
type SavedUpload = { upload_id: string; r2_key: string; part_size: number
                     total_parts: number; name: string; size: number
                     modified: number }
const savedKey = (slug: string, slot: string) => `lead-upload:${slug}:${slot}`
function readSaved(slug: string, slot: string, file: File): SavedUpload | null {
  try {
    const raw = localStorage.getItem(savedKey(slug, slot))
    const v = raw ? JSON.parse(raw) as SavedUpload : null
    return v && v.name === file.name && v.size === file.size
      && v.modified === file.lastModified ? v : null
  } catch { return null }
}
function writeSaved(slug: string, slot: string, v: SavedUpload | null) {
  try {
    if (v) localStorage.setItem(savedKey(slug, slot), JSON.stringify(v))
    else localStorage.removeItem(savedKey(slug, slot))
  } catch { /* blocked storage (private browsing): the Slot keeps it in memory */ }
}
const sameFile = (v: SavedUpload | null, file: File) =>
  !!v && v.name === file.name && v.size === file.size && v.modified === file.lastModified

type SlotPhase =
  | { kind: 'idle' }
  | { kind: 'uploading'; filename: string; loaded: number; total: number; note: string }
  | { kind: 'done' }
  | { kind: 'error'; message: string; resumable?: boolean }

function Slot({ slug, name, cfg }: { slug: string; name: string; cfg: SlotCfg }) {
  const [phase, setPhase] = useState<SlotPhase>({ kind: 'idle' })
  const [dragging, setDragging] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  // Encoded exactly as the config fetch encodes it — the two must never
  // disagree on what address a slug maps to (SDET review, PR #395).
  const pathSlug = encodeURIComponent(slug)

  const fileRef = useRef<File | null>(null)
  // The interrupted upload, in memory too: where localStorage is blocked
  // (private browsing) this is what lets Resume really resume.
  const savedRef = useRef<SavedUpload | null>(null)

  // Leaving mid-upload loses nothing now (the parts stay and resume), but
  // it still stops the upload: say so before the tab closes.
  useEffect(() => {
    if (phase.kind !== 'uploading') return
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [phase.kind])

  const start = async (file: File) => {
    if (file.size > cfg.max_bytes) {
      setPhase({ kind: 'error',
                 message: `File is too large (max ${cfg.max_human}).` })
      return
    }
    fileRef.current = file
    const began = Date.now()
    const note = name === 'video'
      ? 'this can take up to 2 minutes to begin uploading — even if the '
        + 'loading bar is at zero, the process is working'
      : 'preparing upload'
    setPhase({ kind: 'uploading', filename: file.name, loaded: 0,
               total: file.size, note })

    // The same file as an interrupted upload: send only what R2 lacks.
    let upload: SavedUpload | null = readSaved(slug, name, file)
      ?? (sameFile(savedRef.current, file) ? savedRef.current : null)
    const held = new Set<number>()
    if (upload) {
      const res = await withRetry(() => postJSON(
        `${API_BASE}/api/v1/upload/${pathSlug}/resume`,
        { upload_id: upload!.upload_id, r2_key: upload!.r2_key }))
      if (res.ok) {
        const partSize = upload.part_size
        for (const p of (res.data.parts ?? []) as { part_number: number; size: number }[]) {
          const n = p.part_number
          const want = Math.min(partSize, file.size - (n - 1) * partSize)
          if (p.size === want) held.add(n)  // a short part is sent again
        }
      } else {
        upload = null  // gone (aborted or expired): start again
        writeSaved(slug, name, null)
        savedRef.current = null
      }
    }
    if (!upload) {
      const initRes = await postJSON(`${API_BASE}/api/v1/upload/${pathSlug}/init`, {
        slot: name, filename: file.name, size: file.size,
        content_type: file.type || 'application/octet-stream',
      })
      if (!initRes.ok) {
        setPhase({ kind: 'error',
                   message: String(initRes.data.error ?? `init failed (${initRes.status})`) })
        return
      }
      const d = initRes.data as { upload_id: string; r2_key: string
                                  part_size: number; total_parts: number }
      upload = { upload_id: d.upload_id, r2_key: d.r2_key, part_size: d.part_size,
                 total_parts: d.total_parts, name: file.name, size: file.size,
                 modified: file.lastModified }
      writeSaved(slug, name, upload)
      savedRef.current = upload
    }
    const { upload_id, r2_key, part_size, total_parts } = upload

    const parts: { part_number: number; etag: string }[] = []
    let baseLoaded = 0
    for (let n = 1; n <= total_parts; n++) {
      const partStart = (n - 1) * part_size
      const end = Math.min(partStart + part_size, file.size)
      if (held.has(n)) { baseLoaded += end - partStart; continue }
      const blob = file.slice(partStart, end)

      // Patient: each try gets a fresh address (an hour-old one may have
      // expired), waits out an offline browser, and backs off.
      let last: { ok: boolean; status: number; etag: string | null } =
        { ok: false, status: 0, etag: null }
      let attempts = 0
      let presignFailed: string | null = null
      let presignStatus = 0                 // presign-part's answer when it failed
      let refused = false                   // the link or upload said no, for good
      let giveUpAt: number | null = null   // set at the part's first failure
      for (let i = 0; i < PART_ATTEMPTS; i++) {
        attempts = i + 1
        if (giveUpAt !== null) await whenOnline(Math.max(0, giveUpAt - Date.now()))
        const psRes = await withRetry(() => postJSON(
          `${API_BASE}/api/v1/upload/${pathSlug}/presign-part`,
          { upload_id, r2_key, part_number: n }))
        if (!psRes.ok) {
          presignFailed = String(psRes.data?.error ?? `presign part ${n} failed`)
          presignStatus = psRes.status ?? 0
          // a 4xx is the link or the upload refusing: no try will change it
          if (psRes.status >= 400 && psRes.status < 500) { refused = true; break }
        } else {
          presignFailed = null
          last = await putPart(
            String((psRes.data as { upload_url: string }).upload_url), blob,
            (loaded) => setPhase({ kind: 'uploading', filename: file.name,
                                   loaded: baseLoaded + loaded, total: file.size,
                                   note: `part ${n}/${total_parts}`
                                     + (i ? ` · retry ${i}` : '') }),
            giveUpAt === null ? PART_TIMEOUT_MS
              : Math.min(PART_TIMEOUT_MS,
                         Math.max(MIN_RETRY_SEND_MS, giveUpAt - Date.now())))
          if (last.ok) break
        }
        giveUpAt ??= Date.now() + PART_RETRY_WINDOW_MS
        if (i < PART_ATTEMPTS - 1 && Date.now() + retryDelay(i) < giveUpAt) {
          setPhase({ kind: 'uploading', filename: file.name, loaded: baseLoaded,
                     total: file.size,
                     note: `part ${n}/${total_parts} · connection lost, retrying…` })
          await sleep(retryDelay(i))
        } else {
          break
        }
      }
      if (!last.ok) {
        const why = presignFailed ?? describeFailure(last.status)
        // A refusal is final: nothing will accept the rest, so the parts are
        // not kept (the server aborts) and Resume is not offered.
        await postJSON(`${API_BASE}/api/v1/upload/${pathSlug}/fail`, {
          upload_id, r2_key, resumable: !refused,
          reason: `part ${n}: ${why}`,
          progress: { part: n, total_parts, bytes_done: baseLoaded,
                      bytes_total: file.size,
                      elapsed_s: Math.round((Date.now() - began) / 1000),
                      attempts, online: navigator.onLine,
                      // the failing part's last answer: the alert's next
                      // step (who acts) is read from it (_next_step)
                      ...(presignFailed !== null ? { presign_status: presignStatus }
                        : { storage_status: last.status }) },
        }).catch(() => { /* best-effort: the alert is not the upload */ })
        if (refused) {
          writeSaved(slug, name, null)
          savedRef.current = null
          setPhase({ kind: 'error',
                     message: `${why} — this link can no longer accept the upload. `
                       + 'Please contact the person who sent it.' })
          return
        }
        setPhase({ kind: 'error', resumable: true,
                   message: `The connection dropped at part ${n} of ${total_parts}. `
                     + 'What was uploaded is kept for a week — Resume to continue from there.' })
        return
      }
      baseLoaded += end - partStart
      setPhase({ kind: 'uploading', filename: file.name, loaded: baseLoaded,
                 total: file.size, note: `part ${n}/${total_parts}` })
      if (last.etag) {
        parts.push({ part_number: n, etag: last.etag.replace(/"/g, '') })
      }
    }

    setPhase({ kind: 'uploading', filename: file.name, loaded: file.size,
               total: file.size, note: 'finalizing' })
    const completeRes = await withRetry(() => postJSON(
      `${API_BASE}/api/v1/upload/${pathSlug}/complete`, { upload_id, r2_key, parts }))
    if (!completeRes.ok) {
      setPhase({ kind: 'error', resumable: completeRes.status === 409,
                 message: String(completeRes.data.error ?? 'finalize failed') })
      return
    }
    writeSaved(slug, name, null)
    savedRef.current = null
    setPhase({ kind: 'done' })
  }

  const pct = phase.kind === 'uploading' && phase.total > 0
    ? (phase.loaded / phase.total) * 100 : 0

  return (
    <div className="slot-section">
      <div className="slot-header">
        <span className="slot-title">{cfg.human_label}</span>
        <span className={`slot-tag ${name === 'video' ? 'required' : 'optional'}`}>
          {name === 'video' ? 'required' : 'optional'}
        </span>
      </div>

      {(phase.kind === 'idle' || phase.kind === 'error') && (
        <div className={`drop-zone ${name === 'video' ? '' : 'compact'} `
                        + (dragging ? 'dragging' : '')}
             onClick={() => input.current?.click()}
             onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
             onDragLeave={() => setDragging(false)}
             onDrop={(e) => {
               e.preventDefault(); setDragging(false)
               const f = e.dataTransfer.files[0]
               if (f) start(f)
             }}>
          <p className="primary">
            Drop {cfg.human_label.toLowerCase()} here or click to select
          </p>
          <p className="secondary">
            {cfg.allowed_extensions.join(', ')} · up to {cfg.max_human}
          </p>
          <input ref={input} type="file" hidden
                 accept={cfg.allowed_extensions.map((e) => `.${e}`).join(',')}
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) start(f) }} />
        </div>
      )}

      {phase.kind === 'uploading' && (
        <div className="progress-wrap show">
          <div className="progress-filename">{phase.filename}</div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${pct.toFixed(1)}%` }} />
          </div>
          <div className="progress-text">
            {pct.toFixed(1)}% · {(phase.loaded / 1024 / 1024).toFixed(0)} /{' '}
            {(phase.total / 1024 / 1024).toFixed(0)} MB{phase.note ? ` · ${phase.note}` : ''}
          </div>
        </div>
      )}

      {phase.kind === 'done' && (
        <div className="state state-success show">
          <strong>✓ {cfg.human_label} received.</strong> Our team has been notified.
        </div>
      )}
      {phase.kind === 'error' && (
        <div className="state state-error show">
          <strong>{cfg.human_label} upload {phase.resumable ? 'paused' : 'failed'}.</strong>{' '}
          {phase.message}
          {phase.resumable && fileRef.current && (
            <button type="button" className="resume-btn"
                    onClick={() => { const f = fileRef.current; if (f) void start(f) }}>
              Resume upload
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default function LeadUploadPortal() {
  // Rendered pre-auth, OUTSIDE the Router (no session, no BrowserRouter),
  // so the slug comes off the pathname: /upload/<slug>.
  const slug = window.location.pathname.split('/')[2] ?? ''
  const [state, setState] = useState<
    | { kind: 'loading' } | { kind: 'missing' } | { kind: 'error' }
    | { kind: 'ready'; config: Config }>({ kind: 'loading' })

  useEffect(() => {
    let cancelled = false
    // Only a 404 means the link is dead. Anything else — a 500, a dropped
    // connection — is retried like every other call on this page, and
    // after the retries reads as a LOAD error, never as "link gone": a
    // one-second API blip must not tell a prospect their link is dead.
    type CfgFetch = { ok: boolean; status?: number; missing: boolean;
                      config: Config | null }
    withRetry<CfgFetch>(async (): Promise<CfgFetch> => {
      const r = await fetch(
        `${API_BASE}/api/v1/upload/${encodeURIComponent(slug)}/config`,
        { credentials: 'include' })
      if (r.status === 404) return { ok: true, missing: true, config: null }
      if (!r.ok) return { ok: false, status: r.status, missing: false, config: null }
      return { ok: true, missing: false, config: await r.json() as Config }
    }).then((res) => {
      if (cancelled) return
      if (!res.ok) setState({ kind: 'error' })
      else if (res.missing) setState({ kind: 'missing' })
      else setState({ kind: 'ready', config: res.config! })
    })
    return () => { cancelled = true }
  }, [slug])

  if (state.kind === 'missing') {
    return (
      <div className="lead-portal">
        <div className="panel">
          <div className="logo">Priori Sports</div>
          <p className="lead">This upload link is no longer available.</p>
        </div>
      </div>
    )
  }
  if (state.kind === 'error') {
    return (
      <div className="lead-portal">
        <div className="panel">
          <div className="logo">Priori Sports</div>
          <p className="lead">This page could not be loaded. Please refresh
            to try again, or contact{' '}
            <a href="mailto:support@priorisports.com">support@priorisports.com</a>.</p>
        </div>
      </div>
    )
  }
  if (state.kind === 'loading') return null
  const config = state.config

  return (
    <div className="lead-portal">
      <div className="panel">
        <div className="logo">Priori Sports</div>
        <p className="lead">
          {config.lead_name
            ? <>Hi {config.lead_name} — please share your tactical match files below.</>
            : 'Please share your tactical match files below.'}
        </p>
        {Object.entries(config.slots).map(([name, cfg]) => (
          <Slot key={name} slug={slug} name={name} cfg={cfg} />
        ))}
        <div className="requirements">
          <h2>About this upload</h2>
          {/* All six bullets, verbatim from the Jinja page. The first
              three are the only place a lead is told WHICH files to
              send — losing them buys a broadcast feed in the video
              slot and a re-ask by email. */}
          <ul>
            <li><strong>Video</strong> — tactical view: high-angle footage
              showing all 22 players. Different from the broadcast feed
              your league or conference’s streaming/TV station
              provides.</li>
            <li><strong>Tracking data</strong> (optional) — if you already
              have a provider tracking file (.parquet, .json, .xml, .csv)
              from Second Spectrum, Tracab, or similar, drop it in.</li>
            <li><strong>Metadata</strong> (optional) — provider game
              metadata file (.json or .xml) describing rosters, period
              boundaries, frame rate, etc.</li>
            <li>Each slot uploads independently. You can come back and
              re-drop a file if you make a mistake.</li>
            <li>The video upload can take up to 2 minutes to begin uploading
              — even if the loading bar is at zero, the process is
              working.</li>
            <li>For any difficulty uploading, please contact{' '}
              <a href="mailto:support@priorisports.com">support@priorisports.com</a>.</li>
          </ul>
        </div>
      </div>
    </div>
  )
}
